// src/MasterEntry/Component/CustomerDashboard/components/CustomerTyreHistoryModal/index.js
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from "../../../../../AuthContext/Authcontext";
import { useLanguage } from "../../../../../context/LanguageContext";
import API from "../../../../../api/config";
import { cacheService } from '../../../../../services/cacheService';
import { FaTimes, FaDownload, FaSpinner, FaExclamationTriangle, FaInbox } from 'react-icons/fa';
import './CustomerTyreHistoryModal.css';

// ---------- Constants (moved outside) ----------
const statusTranslations = {
  'running': 'running',
  'scrap': 'scrap',
  'replaced': 'replaced',
  'removed_for_repair': 'removed_for_repair',
  'for_new_spare': 'for_new_spare',
  'spare': 'spare',
  'fleet_maintenance': 'fleet_maintenance',
  'rotation': 'rotation',
  'tube_puncture': 'tube_puncture',
  'from_other_mine': 'from_other_mine',
  'repaired': 'repaired',
  'removed': 'removed'
};

const positionTranslations = {
  'fr': 'fr',
  'fl': 'fl',
  'rli': 'rli',
  'rlo': 'rlo',
  'rri': 'rri',
  'rro': 'rro',
  'rr': 'rr',
  'rl': 'rl'
};

const removalReasonTranslations = {
  'air_separation': 'air_separation',
  'bead_damage': 'bead_damage',
  'beyond_repair_center': 'beyond_repair_center',
  'beyond_repair_shoulder': 'beyond_repair_shoulder',
  'beyond_repair_side_wall': 'beyond_repair_side_wall',
  'burst_blow_out': 'burst_blow_out',
  'other': 'other',
  'scrap_due_to_bead_exposed': 'scrap_due_to_bead_exposed',
  'worn_out': 'worn_out',
  'for_repair': 'for_repair',
  'beyond_repair': 'beyond_repair',
  'side_change': 'side_change',
  'meter_change': 'meter_change',
  'scrap_due_to_ply_exposed': 'scrap_due_to_ply_exposed',
  'remove_for_replaced': 'remove_for_replaced',
  'remove_for_fleet_maintenance': 'remove_for_fleet_maintenance',
  'repair_rejected': 'repair_rejected'
};

const typeTranslations = {
  'removal': 'removal',
  'fitment': 'fitment',
  'routine_task': 'routine_task',
  'repaired': 'repaired'
};

const statusBadgeClasses = {
  'running': 'bg-success',
  'scrap': 'bg-danger',
  'replaced': 'bg-warning',
  'removed_for_repair': 'bg-info',
  'for_new_spare': 'bg-primary',
  'spare': 'bg-secondary',
  'fleet_maintenance': 'bg-dark',
  'rotation': 'bg-info',
  'tube_puncture': 'bg-warning',
  'from_other_mine': 'bg-secondary',
  'repaired': 'bg-success',
  'removed': 'bg-danger'
};

const typeBadgeClasses = {
  'fitment': 'badge-success',
  'removal': 'badge-danger',
  'repaired': 'badge-warning',
  'routine task': 'badge-info',
  'routine_task': 'badge-info'
};

// ---------- Loading Component ----------
const LoadingState = ({ t }) => (
  <div className="tyre-history-loading">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">{t('loading') || 'Loading...'}</span>
    </div>
    <p>{t('loading_tyre_history') || 'Loading tyre history...'}</p>
  </div>
);

// ---------- Error Component ----------
const ErrorState = ({ error }) => (
  <div className="tyre-history-error">
    <FaExclamationTriangle className="error-icon" />
    <p>{error}</p>
  </div>
);

// ---------- Empty Component ----------
const EmptyState = ({ t }) => (
  <div className="tyre-history-empty">
    <FaInbox className="empty-icon" />
    <p>{t('no_tyre_history_data') || 'No tyre history data found'}</p>
  </div>
);

// ---------- Tyre Information Component ----------
const TyreInformation = React.memo(({ tyreInfo, getTranslatedStatus, t }) => {
  const infoFields = [
    { key: 'tyre_no', label: 'tyre_number' },
    { key: 'ponumber', label: 'po_number' },
    { key: 'tyre_make', label: 'tyre_make' },
    { key: 'size', label: 'tyre_size' },
    { key: 'tyre_type', label: 'tyre_type' },
    { key: 'otd', label: 'otd' }
  ];

  return (
    <div className="tyre-info-section">
      <h6 className="section-header">{t('tyre_information')}</h6>
      <div className="tyre-info-grid">
        {infoFields.map((field) => (
          <div className="info-row" key={field.key}>
            <span className="info-label">{t(field.label)}:</span>
            <span className="info-value">{tyreInfo[field.key] || '-'}</span>
          </div>
        ))}
        <div className="info-row">
          <span className="info-label">{t('current_status')}:</span>
          <span className={`info-value status-badge ${tyreInfo.status === 'scrap' ? 'status-scrap' : 'status-running'}`}>
            {getTranslatedStatus(tyreInfo.status)}
          </span>
        </div>
      </div>
    </div>
  );
});

TyreInformation.displayName = 'TyreInformation';

// ---------- Summary Component ----------
const SummarySection = React.memo(({ summary, getTranslatedStatus, t }) => {
  const summaryFields = [
    { key: 'current_status', label: 'current_status', isStatus: true },
    { key: 'total_life', label: 'total_life' },
    { key: 'total_repair_life', label: 'total_repair_life' },
    { key: 'requirement_tyre_projection_days', label: 'requirement_days' },
    { key: 'requirement_tyre_projection_month', label: 'requirement_month' }
  ];

  return (
    <div className="tyre-summary-section">
      <h6 className="section-header">{t('summary')}</h6>
      <div className="summary-grid">
        {summaryFields.map((field) => (
          <div className="summary-row" key={field.key}>
            <span className="summary-label">{t(field.label)}:</span>
            <span className={`summary-value ${field.isStatus && summary[field.key] === 'scrap' ? 'text-danger' : 'text-success'}`}>
              {field.isStatus ? getTranslatedStatus(summary[field.key]) : (summary[field.key] || '-')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

SummarySection.displayName = 'SummarySection';

// ---------- Table Row Component ----------
const TableRow = React.memo(({ 
  item, 
  index, 
  indexOfFirstItem, 
  getTranslatedType, 
  getTypeBadgeClass, 
  getTranslatedPosition, 
  getTranslatedRemovalReason, 
  getStatusBadgeClass, 
  getTranslatedStatus 
}) => {
  const translatedType = getTranslatedType(item.type);
  const typeBadgeClass = getTypeBadgeClass(item.type);

  return (
    <tr className={item.type === 'repaired' ? 'row-repaired' : ''}>
      <td className="col-sno">{indexOfFirstItem + index + 1}</td>
      <td className="col-site">{item.site_name || '-'}</td>
      <td className="col-project">{item.project || '-'}</td>
      <td className="col-machine">{item.machine_no || '-'}</td>
      <td className="col-truck-make">{item.truck_make || '-'}</td>
      <td className="col-truck-model">{item.truck_modal || '-'}</td>
      <td className="col-position">{getTranslatedPosition(item.position)}</td>
      <td className="col-service-date">{item.service_date || '-'}</td>
      <td className="col-type">
        <span className={`type-badge ${typeBadgeClass}`}>
          {translatedType}
        </span>
      </td>
      <td className="col-pressure">{item.pressure || item.tyre_pressure || '-'}</td>
      <td className="col-rtd-a">{item.rtd_a !== '-' ? item.rtd_a : '-'}</td>
      <td className="col-rtd-b">{item.rtd_b !== '-' ? item.rtd_b : '-'}</td>
      <td className="col-current-hmr">{item.current_hmr !== '-' ? item.current_hmr : '-'}</td>
      <td className="col-km-cipm">{item.hcipm !== '-' ? item.hcipm : '-'}</td>
      <td className="col-km-cicm">{item.hcicm !== '-' ? item.hcicm : '-'}</td>
      <td className="col-repair-life">{item.repair_life !== '-' ? item.repair_life : '-'}</td>
      <td className="col-count">{item.fitment_removal_count || '-'}</td>
      <td className="col-removal-reason">{getTranslatedRemovalReason(item.removal_reason)}</td>
      <td className="col-status">
        <span className={`badge ${getStatusBadgeClass(item.status)}`}>
          {getTranslatedStatus(item.status)}
        </span>
      </td>
    </tr>
  );
});

TableRow.displayName = 'TableRow';

// ---------- Main Component ----------
const CustomerTyreHistoryModal = ({ isOpen, onClose, filters, tyreSize, tyreNo }) => {
  const { t, language } = useLanguage();
  const { user } = useContext(AuthContext);
  
  // ---------- State ----------
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const abortControllerRef = useRef(null);
  const dataFetchedRef = useRef(false);

  // ---------- Memoized Translation Functions ----------
  const getTranslatedStatus = useCallback((status) => {
    if (!status) return '-';
    const key = status.toLowerCase().trim();
    const translationKey = statusTranslations[key] || key;
    return t(translationKey) || status;
  }, [t]);

  const getTranslatedPosition = useCallback((position) => {
    if (!position) return '-';
    const key = position.toLowerCase().trim();
    const translationKey = positionTranslations[key] || key;
    return t(translationKey) || position;
  }, [t]);

  const getTranslatedRemovalReason = useCallback((reason) => {
    if (!reason) return '-';
    let key = reason.toLowerCase().trim();
    key = key.replace(/-/g, '_');
    key = key.replace(/\//g, '_');
    key = key.replace(/ /g, '_');
    key = key.replace(/_+/g, '_');
    const translationKey = removalReasonTranslations[key] || key;
    const translated = t(translationKey);
    if (!translated || translated === translationKey) {
      return reason;
    }
    return translated;
  }, [t]);

  const getTranslatedType = useCallback((type) => {
    if (!type) return 'Routine task';
    const key = type.toLowerCase().trim().replace(/ /g, '_');
    const translationKey = typeTranslations[key] || key;
    return t(translationKey) || type;
  }, [t]);

  const getTypeBadgeClass = useCallback((type) => {
    if (!type) return 'badge-info';
    const key = type.toLowerCase().trim();
    return typeBadgeClasses[key] || 'badge-info';
  }, []);

  const getStatusBadgeClass = useCallback((status) => {
    if (!status) return 'bg-secondary';
    const key = status.toLowerCase();
    return statusBadgeClasses[key] || 'bg-secondary';
  }, []);

  // ---------- Fetch Tyre History ----------
  const fetchTyreHistory = useCallback(async () => {
    if (!isOpen || !user?.access_token || !tyreNo) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const cacheKey = `tyre_history_${tyreNo}_${language}`;
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      setData(cachedData);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const params = {
        login_user_id: user?.userid,
        role_id: user?.role_id,
        parent_id: user?.parent_id || "",
        tyre_no: tyreNo || "",
        language: language
      };

      const response = await API.get('particularTyreHistory', {
        headers: { Authorization: `Bearer ${user?.access_token}` },
        params,
        signal: abortControllerRef.current.signal
      });

      if (response.data?.Status && response.data?.data) {
        const historyData = response.data.data;
        cacheService.set(cacheKey, historyData, 10 * 60 * 1000);
        setData(historyData);
        setError(null);
      } else {
        setData(null);
        setError(response.data?.msg || 'No data found');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching tyre history:', error);
      setError('Failed to load tyre history');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isOpen, user, tyreNo, language]);

  // ---------- Download Handler ----------
  const handleDownload = useCallback(async () => {
    if (!user?.access_token || !tyreNo) return;

    setDownloading(true);
    try {
      const params = {
        login_user_id: user?.userid,
        role_id: user?.role_id,
        parent_id: user?.parent_id || "",
        tyre_no: tyreNo || "",
        language: language
      };

      const response = await API.post(
        'particularTyreHistory_Excel',
        params,
        {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob',
          timeout: 60000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TyreHistory_${tyreNo || 'Report'}_${language}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
    } finally {
      setDownloading(false);
    }
  }, [user, tyreNo, language]);

  // ---------- Effects ----------
  useEffect(() => {
    if (isOpen) {
      fetchTyreHistory();
      setCurrentPage(1);
      dataFetchedRef.current = false;
    }
  }, [isOpen, fetchTyreHistory]);

  // ---------- Cleanup ----------
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ---------- Memoized Computed Values ----------
  const tyreInfo = data?.tyre_information;
  const performanceData = data?.tyre_performance_info || [];
  const summary = data?.summary;

  const { indexOfFirstItem, indexOfLastItem, currentItems, totalPages } = useMemo(() => {
    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    return {
      indexOfFirstItem: indexOfFirst,
      indexOfLastItem: indexOfLast,
      currentItems: performanceData.slice(indexOfFirst, indexOfLast),
      totalPages: Math.ceil(performanceData.length / itemsPerPage)
    };
  }, [performanceData, currentPage, itemsPerPage]);

  // ---------- Render ----------
  if (!isOpen) return null;

  // Modal content
  const modalContent = (
    <div 
      className="tyre-history-modal-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="tyre-history-modal-container">
        {/* Header */}
        <div className="tyre-history-modal-header">
          <div className="tyre-history-modal-header-content">
            <h5 className="tyre-history-modal-title">
              {t('tyre_history') || 'Tyre History'} - {tyreNo || ''}
            </h5>
          </div>
          <div className="tyre-history-modal-header-actions">
            <button 
              className="btn btn-sm btn-light"
              onClick={handleDownload}
              disabled={downloading}
              title={t('download_report') || 'Download Report'}
            >
              {downloading ? (
                <FaSpinner className="fa-spin" />
              ) : (
                <FaDownload />
              )}
              <span className="ms-1">{t('export') || 'Export'}</span>
            </button>
            <button 
              type="button" 
              className="tyre-history-modal-close-btn" 
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="tyre-history-modal-body">
          {loading ? (
            <LoadingState t={t} />
          ) : error ? (
            <ErrorState error={error} />
          ) : !tyreInfo ? (
            <EmptyState t={t} />
          ) : (
            <div className="tyre-history-scroll-container">
              {/* Tyre Information */}
              <TyreInformation 
                tyreInfo={tyreInfo} 
                getTranslatedStatus={getTranslatedStatus}
                t={t}
              />

              {/* Performance Table */}
              {performanceData.length > 0 && (
                <div className="tyre-performance-section">
                  <h6 className="section-header">{t('tyre_history')}</h6>
                  <div className="tyre-history-table-wrapper">
                    <div className="tyre-history-table-scroll">
                      <table className="tyre-history-table">
                        <thead>
                          <tr>
                            <th className="col-sno">{t('s_no') || 'S.NO'}</th>
                            <th className="col-site">{t('site') || 'Site'}</th>
                            <th className="col-project">{t('project') || 'Project'}</th>
                            <th className="col-machine">{t('machine_number') || 'Machine No.'}</th>
                            <th className="col-truck-make">{t('truck_make') || 'Truck Make'}</th>
                            <th className="col-truck-model">{t('truck_model') || 'Truck Model'}</th>
                            <th className="col-position">{t('position') || 'Position'}</th>
                            <th className="col-service-date">{t('service_date') || 'Service Date'}</th>
                            <th className="col-type">{t('type') || 'Type'}</th>
                            <th className="col-pressure">{t('pressure') || 'Pressure'}</th>
                            <th className="col-rtd-a">{t('rtd_a') || 'RTD A (mm)'}</th>
                            <th className="col-rtd-b">{t('rtd_b') || 'RTD B (mm)'}</th>
                            <th className="col-current-hmr">{t('current_hmr') || 'Current KMR'}</th>
                            <th className="col-km-cipm">{t('km_cipm') || 'KM covered in previous machine'}</th>
                            <th className="col-km-cicm">{t('km_cicm') || 'KM covered in current machine'}</th>
                            <th className="col-repair-life">{t('repair_life') || 'Repair Life'}</th>
                            <th className="col-count">{t('count') || 'Count'}</th>
                            <th className="col-removal-reason">{t('removal_reason') || 'Removal Reason'}</th>
                            <th className="col-status">{t('status') || 'Status'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((item, index) => (
                            <TableRow
                              key={`${item.id || index}-${index}`}
                              item={item}
                              index={index}
                              indexOfFirstItem={indexOfFirstItem}
                              getTranslatedType={getTranslatedType}
                              getTypeBadgeClass={getTypeBadgeClass}
                              getTranslatedPosition={getTranslatedPosition}
                              getTranslatedRemovalReason={getTranslatedRemovalReason}
                              getStatusBadgeClass={getStatusBadgeClass}
                              getTranslatedStatus={getTranslatedStatus}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="tyre-history-pagination">
                      <div className="pagination-info">
                        {t('showing') || 'Showing'} {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, performanceData.length)} {t('of') || 'of'} {performanceData.length} {t('records') || 'records'}
                      </div>
                      <nav>
                        <ul className="pagination-list">
                          <li>
                            <button 
                              className="pagination-btn"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              aria-label="Previous page"
                            >
                              «
                            </button>
                          </li>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <li key={pageNum}>
                                <button 
                                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                                  onClick={() => setCurrentPage(pageNum)}
                                  aria-label={`Page ${pageNum}`}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          <li>
                            <button 
                              className="pagination-btn"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              aria-label="Next page"
                            >
                              »
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}
              {summary && (
                <SummarySection 
                  summary={summary} 
                  getTranslatedStatus={getTranslatedStatus}
                  t={t}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tyre-history-modal-footer">
          <button type="button" className="btn-close-modal" onClick={onClose}>
            {t('close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default React.memo(CustomerTyreHistoryModal);